/**
 * Message Deduplication Utilities
 * 
 * Consolidated logic for detecting and handling duplicate messages
 * between Firestore and queued messages.
 */

/**
 * Check if a queued message is a duplicate of a Firestore message
 * @param {Object} firestoreMsg - Message from Firestore
 * @param {Object} queueMsg - Message from local queue
 * @param {number} timeThreshold - Time difference threshold in milliseconds (default: 5000ms)
 * @returns {boolean} True if messages are duplicates
 */
export const isDuplicateMessage = (firestoreMsg, queueMsg, timeThreshold = 5000) => {
  if (!firestoreMsg || !queueMsg) return false;

  // FIX C6: Guard against null timestamps - new Date(null) → NaN/epoch, causing false dedup
  if (!firestoreMsg.createdAt) return false;

  const senderMatch = firestoreMsg.senderId === queueMsg.senderId;
  const contentMatch = firestoreMsg.content === queueMsg.content;

  // Check tempId match for stable identification (highest priority)
  const tempIdMatch = firestoreMsg.tempId && queueMsg.tempId &&
    firestoreMsg.tempId === queueMsg.tempId;

  // If tempIds match, this is definitely the same message
  if (tempIdMatch) return true;

  // Calculate time difference
  const firestoreTime = firestoreMsg.createdAt?.toMillis
    ? firestoreMsg.createdAt.toMillis()
    : firestoreMsg.createdAt?.getTime
      ? firestoreMsg.createdAt.getTime()
      : new Date(firestoreMsg.createdAt).getTime();

  const queueTime = typeof queueMsg.createdAt === 'number'
    ? queueMsg.createdAt
    : new Date(queueMsg.createdAt).getTime();

  const timeMatch = Math.abs(firestoreTime - queueTime) < timeThreshold;

  return senderMatch && contentMatch && timeMatch;
};

/**
 * Check if a queued message has been confirmed by Firestore (via realId or tempId)
 * @param {Object} queueMsg - Message from local queue
 * @param {Set<string>} firestoreIds - Set of Firestore message IDs
 * @param {Map<string, string>} tempIdToRealIdMap - Map of tempId to real Firestore ID
 * @returns {boolean} True if message is confirmed in Firestore
 */
export const isMessageConfirmed = (queueMsg, firestoreIds, tempIdToRealIdMap = null) => {
  // Check if realId exists and is in Firestore
  if (queueMsg.realId && firestoreIds.has(queueMsg.realId)) {
    return true;
  }

  // Check if tempId maps to a Firestore ID
  if (queueMsg.tempId && tempIdToRealIdMap) {
    const mappedRealId = tempIdToRealIdMap.get(queueMsg.tempId);
    if (mappedRealId && firestoreIds.has(mappedRealId)) {
      return true;
    }
  }

  return false;
};

/**
 * Get a stable key for a message (for React key prop)
 * Uses tempId if available, otherwise falls back to id
 * @param {Object} msg - Message object
 * @returns {string} Stable key for the message
 */
export const getStableMessageKey = (msg) => {
  if (!msg) return '';
  // Use tempId as the stable key since it's set on both optimistic and Firestore messages
  return msg.tempId || msg.id;
};

/**
 * Build a map of tempId to real Firestore ID from messages
 * @param {Array} messages - Array of messages (can include both Firestore and optimistic)
 * @returns {Map<string, string>} Map of tempId to realId
 */
export const buildTempIdMap = (messages) => {
  const map = new Map();
  messages.forEach(msg => {
    if (msg.tempId && msg.id && !msg.id.startsWith('temp_')) {
      map.set(msg.tempId, msg.id);
    }
  });
  return map;
};

/**
 * Merge Firestore messages with queued messages, removing duplicates
 * @param {Array} firestoreMsgs - Messages from Firestore
 * @param {Array} queuedMsgs - Messages from local queue
 * @param {Function} onRemoveQueued - Callback to remove a queued message (receives queueMsg.id)
 * @returns {Array} Merged and deduplicated message array
 */
export const mergeMessagesWithQueue = (firestoreMsgs, queuedMsgs, onRemoveQueued) => {
  const firestoreIds = new Set(firestoreMsgs.map(m => m.id));

  // Build tempId to realId map from Firestore messages
  const tempIdToRealIdMap = new Map();
  firestoreMsgs.forEach(msg => {
    if (msg.tempId && msg.id) {
      tempIdToRealIdMap.set(msg.tempId, msg.id);
    }
  });

  // Start with Firestore messages
  const merged = [...firestoreMsgs];

  // FIX 6.2: Collect IDs to remove in first pass, execute side-effects after
  const toRemove = [];

  // Add queued messages that aren't duplicates
  queuedMsgs.forEach(qMsg => {
    // Remove if confirmed in Firestore (by realId or tempId mapping)
    if (isMessageConfirmed(qMsg, firestoreIds, tempIdToRealIdMap)) {
      toRemove.push(qMsg.id);
      return;
    }

    // Remove if duplicate of any Firestore message
    const isDuplicate = firestoreMsgs.some(fMsg => isDuplicateMessage(fMsg, qMsg));
    if (isDuplicate) {
      toRemove.push(qMsg.id);
      return;
    }

    // Keep this queued message
    merged.push(qMsg);
  });

  // Execute side-effects after iteration is complete
  toRemove.forEach(id => onRemoveQueued?.(id));

  return merged;
};

/**
 * Sort messages by creation time (handles both Firestore timestamps and numeric timestamps)
 * @param {Array} messages - Array of messages to sort
 * @returns {Array} Sorted array (ascending by time)
 */
export const sortMessagesByTime = (messages) => {
  return [...messages].sort((a, b) => {
    const getTime = (msg) => {
      // FIX 6.1: Use Date.now() for null/invalid dates so new messages sort to end
      if (!msg?.createdAt) return Date.now();
      if (msg.createdAt.toDate) return msg.createdAt.toDate().getTime();
      if (msg.createdAt.getTime) return msg.createdAt.getTime();
      if (typeof msg.createdAt === 'number') return msg.createdAt;
      const parsed = new Date(msg.createdAt).getTime();
      return Number.isNaN(parsed) ? Date.now() : parsed;
    };

    return getTime(a) - getTime(b);
  });
};
